import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  Paper,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import CertificationsService from "../../api/certificationsService";

const CreateCertificationPage = () => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      icon: "☁️",
      obtainedDate: new Date(),
      isActive: true,
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      // Format the date as ISO string for the backend
      const formattedData = {
        ...data,
        obtainedDate: data.obtainedDate.toISOString().split("T")[0], // YYYY-MM-DD format
      };

      const token = localStorage.getItem("token");
      await CertificationsService.create(formattedData, token);
      navigate("/admin/certifications");
    } catch (error) {
      console.error("Error creating certification:", error);
      alert("Failed to create certification. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Create New Certification
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item size={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Certification name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Certification Name"
                    error={!!errors.name}
                    helperText={
                      errors.name?.message || "e.g., AWS Certified Developer"
                    }
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="icon"
                control={control}
                rules={{ required: "Icon is required" }}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.icon}>
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Icon"
                      error={!!errors.icon}
                      helperText={errors.icon?.message || "Enter an image URL"}
                    />
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item size={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Controller
                  name="obtainedDate"
                  control={control}
                  rules={{ required: "Date is required" }}
                  render={({ field }) => (
                    <DatePicker
                      label="Date Obtained"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.obtainedDate,
                          helperText:
                            errors.obtainedDate?.message ||
                            "When did you receive this certification?",
                        },
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item size={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch checked={field.value} onChange={field.onChange} />
                    }
                    label="Active certification"
                  />
                )}
              />
              <FormHelperText>
                Toggle off if the certification is expired or not currently
                valid
              </FormHelperText>
            </Grid>

            <Grid item size={12} sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <CircularProgress size={24} />
                ) : (
                  "Create Certification"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/certifications")}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateCertificationPage;
